import { motion } from 'framer-motion';

const steps = [
  {
    number: '01',
    phase: 'Phase 1',
    duration: '1-2 jours',
    title: 'Contact et audit',
    description: 'Prise de contact, visite sur site et réalisation de l\'audit énergétique gratuit',
    icon: 'ri-phone-line',
    color: 'from-emerald-500 to-teal-500',
    image: 'https://public.readdy.ai/ai/img_res/b76a51abc3f0c402e2fad0a9d985d113.jpg',
    features: [
      'Prise de contact rapide',
      'Visite technique complète',
      'Audit énergétique gratuit'
    ]
  },
  {
    number: '02',
    phase: 'Phase 2', 
    duration: '5-7 jours',
    title: 'Étude et devis',
    description: 'Réalisation de l\'étude photométrique, constitution du dossier CEE et devis détaillé',
    icon: 'ri-file-text-line',
    color: 'from-teal-500 to-cyan-500',
    image: 'https://readdy.ai/api/search-image?query=professional%20photometric%20study%20lighting%20simulation%20software%20on%20computer%20screen%20showing%20industrial%20warehouse%203D%20model%20with%20light%20distribution%20analysis%20technical%20engineering%20workspace%20modern%20office%20setting&width=600&height=400&seq=study-step-002&orientation=landscape',
    features: [
      'Étude photométrique 3D',
      'Constitution dossier CEE',
      'Devis détaillé personnalisé'
    ]
  },
  {
    number: '03',
    phase: 'Phase 3',
    duration: '2-3 semaines', 
    title: 'Validation et commande',
    description: 'Validation du projet, signature du devis et commande des équipements LED',
    icon: 'ri-checkbox-circle-line',
    color: 'from-cyan-500 to-blue-500',
    image: 'https://readdy.ai/api/search-image?query=business%20handshake%20contract%20signing%20professional%20meeting%20office%20environment%20document%20validation%20project%20approval%20modern%20workspace&width=600&height=400&seq=validation-step-003&orientation=landscape',
    features: [
      'Validation du projet',
      'Signature du devis',
      'Commande équipements'
    ]
  },
  {
    number: '04', 
    phase: 'Phase 4',
    duration: '1-2 jours',
    title: 'Installation',
    description: 'Installation des luminaires LED par nos techniciens certifiés et mise en service',
    icon: 'ri-tools-line',
    color: 'from-blue-500 to-indigo-500',
    image: 'https://readdy.ai/api/search-image?query=professional%20electrician%20installing%20LED%20high%20bay%20lights%20in%20industrial%20warehouse%20wearing%20safety%20equipment%20professional%20installation%20work%20technical%20expertise%20bright%20well%20lit%20environment&width=600&height=400&seq=install-step-004&orientation=landscape',
    features: [
      'Techniciens certifiés',
      'Installation professionnelle',
      'Mise en service complète'
    ]
  },
  {
    number: '05',
    phase: 'Phase 5',
    duration: '1 jour',
    title: 'Finalisation', 
    description: 'Tests de fonctionnement, formation utilisateur et remise de la garantie 5 ans',
    icon: 'ri-award-line',
    color: 'from-indigo-500 to-purple-500',
    image: 'https://readdy.ai/api/search-image?query=professional%20technician%20testing%20LED%20lighting%20system%20with%20measuring%20equipment%20quality%20control%20final%20inspection%20modern%20industrial%20setting%20professional%20warranty%20documentation&width=600&height=400&seq=final-step-005&orientation=landscape',
    features: [
      'Tests de fonctionnement',
      'Formation utilisateur',  
      'Garantie 5 ans'
    ]
  }
];

export default function StepsSection() {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12 lg:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">
            Nos Services
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Détaillés</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-3xl mx-auto">
            Un accompagnement professionnel à chaque étape de votre projet de rénovation LED
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="space-y-6 sm:space-y-8 lg:space-y-12">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="flex items-center gap-3 mb-3 sm:mb-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center`}>
                    <i className={`${step.icon} text-white text-lg sm:text-xl`}></i>
                  </div>
                  <div>
                    <span className="inline-flex items-center justify-center text-xs sm:text-sm font-bold text-white tracking-wider uppercase px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full shadow-lg border-2 border-emerald-500">
                      {step.phase}
                    </span>
                  </div>
                </div>
                
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-emerald-600 to-teal-600 mb-2 sm:mb-3 leading-tight">
                  {step.title}
                </h3>
                
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                  <span className="text-xs sm:text-sm text-emerald-600 font-semibold">
                    Durée : {step.duration}
                  </span>
                </div>
                
                <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6 leading-relaxed">
                  {step.description}
                </p>

                {/* Features - Mobile optimized */}
                <div className="space-y-2 sm:space-y-3">
                  {step.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-lg border border-slate-200 hover:border-emerald-200 transition-all">
                      <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex-shrink-0"></div>
                      <span className="text-xs sm:text-sm text-slate-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''} order-first lg:order-none`}>
                <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                  <div className="w-full h-48 sm:h-64 lg:h-80">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
                  
                  {/* Step Number Overlay */}
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                      <span className="text-white font-bold text-sm sm:text-base">{step.number}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}